import { ButtonBase, Dialog as MuiDialog, styled } from '@mui/material';

// Restrained gold accent — the premium signal on the navy Pro tier.
export const GOLD_ACCENT = '#F5C451';

/** The Pro pitch sheet's width on desktop, shared by every surface that hosts
 * it -- this dialog, the AI-credit dialog, the tailor dialog. Wide enough that
 * an answer and its tags sit on a line or two, not a column. */
export const PRO_PITCH_WIDTH = 640;

export const Dialog = styled(MuiDialog, {
  shouldForwardProp: (prop) => prop !== 'noPadding' && prop !== 'pitch',
})<{ noPadding?: boolean; pitch?: boolean }>(({ theme, noPadding, pitch }) => ({
  '& .MuiPaper-root': {
    // Matches CreditsSlider's own compact CardRoot radius (18px) so the card
    // fills the paper edge-to-edge with no visible seam when noPadding.
    borderRadius: noPadding ? '18px' : '20px',
    padding: noPadding ? 0 : '16px',
    overflow: noPadding ? 'hidden' : undefined,
    // Shrink to the card's own width instead of a fixed one when noPadding — the
    // card (and the "Go for Pro" link below it) already size themselves; the
    // card's own boxShadow/border are enough, the Paper's default elevation
    // shadow on top of that reads as a doubled outline.
    //
    // 560px for the padded (Pro pitch) variant: the plan lists seven benefits in
    // two columns, and at 470px each cell wrapped to two lines, which turned a
    // scannable grid back into a wall of text.
    maxWidth: noPadding ? 'fit-content' : '620px',
    width: '100%',
    boxShadow: noPadding ? 'none' : undefined,
    border: noPadding ? 'none' : undefined,
  },
  // The Pro pitch only. It owns its own header/list/footer layout, so it takes
  // a wider paper and sizes to its content.
  ...(pitch && {
    '& .MuiPaper-root': {
      maxWidth: `${PRO_PITCH_WIDTH}px`,
      width: '100%',
      // Sized to the pitch, never stretched: a full-height sheet framed the
      // list in empty paper on a tall window. Scrolls inside only when the
      // viewport can't hold it.
      maxHeight: 'calc(100% - 64px)',
      padding: 0,
      borderRadius: '24px',
      overflow: 'hidden',
      // The pass paints its own surface (paper plus two accent washes), so the
      // Paper stays out of the way.
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      boxShadow: '0 30px 70px rgba(16,24,40,.28)',
    },
  }),
  [theme.breakpoints.down('sm')]: {
    '& .MuiPaper-root': {
      margin: '16px',
    },
    // Full-bleed on a phone: at this height a margin-boxed sheet leaves slivers
    // of backdrop top and bottom and the footer stops looking anchored.
    ...(pitch && {
      '& .MuiPaper-root': {
        margin: 0,
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        maxHeight: '100%',
        borderRadius: 0,
      },
    }),
  },
}));

// Quiet text link — used for secondary escapes (top-up, go-for-pro) that
// shouldn't visually compete with the primary CTA above them.
export const SubtleLinkButton = styled(ButtonBase)(({ theme }) => ({
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.text.primary },
}));

// Primary-colored text link — used for actions the user is meant to notice
// (changing the billing address).
// A small pill rather than a bare link: it toggles the address shutter, and a
// toggle needs a hit area you can see. Gold-tinted, like the rest of the sheet.
export const PrimaryLinkButton = styled(ButtonBase)(({ theme }) => ({
  fontFamily: 'inherit',
  fontSize: 12.5,
  fontWeight: 800,
  padding: '6px 12px',
  borderRadius: '100px',
  color: theme.palette.brand.goldInk,
  backgroundColor: theme.palette.guidedCourse.goldTint,
  border: `1px solid ${theme.palette.guidedCourse.goldTintBorder}`,
  '&:hover': { filter: 'brightness(0.97)' },
  ...theme.applyStyles('dark', {
    color: theme.palette.guidedCourse.gold,
    backgroundColor: 'rgba(255,176,32,.10)',
    border: `1px solid rgba(255,176,32,.28)`,
  }),
}));
